//! SSE-клиент для пошагового получения ответа чата.
//!
//! RTK Query не умеет streaming, поэтому здесь — прямой fetch с
//! `ReadableStream`-парсером. Helper выдаёт AsyncGenerator событий
//! `delta` / `done` / `error`, потребитель оборачивает в `for await`.
//!
//! Auth: token берётся из общего store; на 401 один раз пытаемся
//! `refreshAccessToken` и повторяем запрос. Логика согласована с
//! `baseQueryWithRefresh` — один источник истины для refresh-мьютекса.

import { store } from '@app/store';
import { refreshAccessToken } from '@features/auth/model/refresh';
import { clearTokens } from '@features/auth/model/slice';
import type { AppDispatch, RootState } from '@app/store';

const API_BASE: string = (import.meta.env.VITE_API_BASE_URL as string | undefined) ?? '/api/v1';

export interface SseDelta {
  event: 'delta';
  content: string;
}
export interface SseDone {
  event: 'done';
  messageId: string;
  references: string[];
  createdAt: string;
}
export interface SseError {
  event: 'error';
  error: string;
}
export type SseChatEvent = SseDelta | SseDone | SseError;

async function makeRequest(sessionId: string, content: string, token: string | null): Promise<Response> {
  return fetch(`${API_BASE}/agents/statut/chat/sessions/${sessionId}/messages/stream`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'text/event-stream',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify({ content }),
  });
}

/**
 * Стримит ответ агента на сообщение пользователя. Использовать через:
 *
 *   for await (const ev of streamChatMessage(sessionId, content)) {
 *     if (ev.event === 'delta') append(ev.content);
 *     else if (ev.event === 'done') invalidateCache();
 *   }
 */
export async function* streamChatMessage(
  sessionId: string,
  content: string,
): AsyncGenerator<SseChatEvent, void, void> {
  // Первый запрос с текущим токеном; на 401 — refresh и retry.
  let token = (store.getState() as RootState).auth.accessToken;
  let resp = await makeRequest(sessionId, content, token);

  if (resp.status === 401) {
    const ok = await refreshAccessToken(
      store.dispatch as AppDispatch,
      store.getState as () => RootState,
    );
    if (!ok) {
      store.dispatch(clearTokens());
      yield { event: 'error', error: 'Authentication failed' };
      return;
    }
    token = (store.getState() as RootState).auth.accessToken;
    resp = await makeRequest(sessionId, content, token);
  }

  if (!resp.ok || !resp.body) {
    const text = await resp.text().catch(() => '');
    yield { event: 'error', error: `HTTP ${resp.status}: ${text.slice(0, 200)}` };
    return;
  }

  const reader = resp.body.getReader();
  const decoder = new TextDecoder('utf-8');
  let buffer = '';
  try {
    while (true) {
      const { value, done } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      // SSE — события разделены `\n\n`. Один event может занимать несколько
      // строк (`event: ...\ndata: ...\n`).
      let sep: number;
      while ((sep = buffer.indexOf('\n\n')) !== -1) {
        const block = buffer.slice(0, sep);
        buffer = buffer.slice(sep + 2);
        const ev = parseSseBlock(block);
        if (ev) yield ev;
      }
    }
    // Возможный хвост без trailing \n\n
    const tail = buffer.trim();
    if (tail.length > 0) {
      const ev = parseSseBlock(tail);
      if (ev) yield ev;
    }
  } finally {
    try {
      reader.releaseLock();
    } catch {
      // ignore
    }
  }
}

function parseSseBlock(block: string): SseChatEvent | null {
  let eventName = 'message';
  const dataLines: string[] = [];
  for (const line of block.split('\n')) {
    if (line.startsWith('event:')) {
      eventName = line.slice(6).trim();
    } else if (line.startsWith('data:')) {
      dataLines.push(line.slice(5).trim());
    }
    // ':' (comment), 'id:', 'retry:' — игнорируем
  }
  if (dataLines.length === 0) return null;
  const rawData = dataLines.join('\n');
  let payload: Record<string, unknown>;
  try {
    payload = JSON.parse(rawData);
  } catch {
    return null;
  }
  switch (eventName) {
    case 'delta':
      return {
        event: 'delta',
        content: typeof payload.delta === 'string' ? payload.delta : '',
      };
    case 'done':
      return {
        event: 'done',
        messageId: String(payload.message_id ?? ''),
        references: Array.isArray(payload.references) ? (payload.references as string[]) : [],
        createdAt: String(payload.created_at ?? ''),
      };
    case 'error':
      return {
        event: 'error',
        error: String(payload.error ?? 'unknown error'),
      };
    default:
      return null;
  }
}
